import React, { useEffect, useState, useRef, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import _ from "lodash"
import { DateTime } from "luxon"
import { apiService } from "../../api/axiosInstance"

import { Button } from "primereact/button"
import { SplitButton } from "primereact/splitbutton"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { Toast } from "primereact/toast"
import { Skeleton } from "primereact/skeleton"


import useSubscribeAllDocs from "../../hooks/useSubscribeAllDocs"
import handleStatusMessage from "../../helpers/handleStatusMessage"
import Breadcrumb from "../../components/Breadcrumb"

export default function ProjectsAndDataOrders() {
  const [deletingDataOrder, setDeletingDataOrder] = useState(false)
  const [fetching, setFetching] = useState(true)
  const [projectMetadata, setProjectMetadata] = useState([])

  const [pages, setPages] = useState([])
  const [currentPageIndex, setCurrentPageIndex] = useState(0)

  const hookParams = {
    collection: "mrf_orders",
    filter: ["mrf_type", "==", "in_network"],
    limit: 15,
    startAfterDoc: pages[currentPageIndex - 1] || null,
  }

  const [loading, error, data, lastVisible] = useSubscribeAllDocs(hookParams)

  const navigate = useNavigate()
  const toast = useRef(null)

  useEffect(() => {
    if (lastVisible && pages[currentPageIndex] !== lastVisible) {
      const newPages = [...pages]
      newPages[currentPageIndex] = lastVisible
      setPages(newPages)
    }
  }, [lastVisible])

  const dataIdsKey = useMemo(() => JSON.stringify(data?.map(d => d.id) || []), [data])
  const prevIdsKeyRef = useRef(null)

  useEffect(() => {
    if (!data) return
    if (prevIdsKeyRef.current === dataIdsKey) return
    prevIdsKeyRef.current = dataIdsKey
    const getProjectMetadata = async () => {
      const projectMetadataList = await Promise.all(
        _.map(data, async ({ id: mrf_order_id }) => {
          try {
            const res = await apiService.get(`/mrf-orders/${mrf_order_id}/metadata`)
            const fetchData = res.data.data
            fetchData.mrf_order_id = mrf_order_id
            return fetchData
          } catch (error) {
            return null
          }
        })
      )
      setProjectMetadata(projectMetadataList)
      setFetching(false)
    }
    getProjectMetadata()
  }, [data, dataIdsKey])

  const handleData = useMemo(() => {
    if (!data) return []
    return _.map(data, (row) => {
      const currentProjectMetadata =
        _.find(projectMetadata, { mrf_order_id: row.id }) || {}

      const handleDelete = async (docId) => {
        try {
          setDeletingDataOrder(true)
          const response = await apiService.delete(`/data-orders/${docId}`)
          setDeletingDataOrder(false)
          if (response.data.status === "success") {
            toast.current.show({
              severity: "success",
              summary: "Data Order Deleted",
              detail: response.data.message,
              life: 3000,
            })
          } else {
            toast.current.show({
              severity: "error",
              summary: "Error while trying to delete Data Order",
              detail: "The document was not successfully deleted.",
              life: 3000,
            })
          }
        } catch (error) {
          toast.current.show({
            severity: "error",
            summary: "Error Deleting Document",
            detail: "An error occurred while deleting the document.",
            life: 3000,
          })
        }
      }

      const handleActions = () => {
        const {
          data_order_id: docId,
          data_order_status: status,
          project_alias,
        } = currentProjectMetadata

        const items = [
          {
            label: "View Dataset",
            icon: "pi pi-eye",
            command: () => navigate(`/project/details/${project_alias}`),
          },
          {
            label: "Add MRF to Data Order",
            icon: "pi pi-plus",
            command: () => navigate(`/data-order/edit/${docId}?screen=3`),
          },
          {
            label: "Delete",
            icon: "pi pi-times",
            command: () => handleDelete(docId),
          },
        ]
        return (
          <SplitButton
            label="View Order"
            onClick={() => navigate(`/data-order/details/${docId}/${status}`)}
            model={items}
            disabled={deletingDataOrder}
          />
        )
      }

      const handleMrfOrderName = (name) => {
        if (!name) return
        const maxLength = 24
        return name.length > maxLength
          ? name.substring(0, maxLength) + "..."
          : name
      }

      const modDate = row.timestamp_updated?.toDate?.()

      return {
        ...row,
        mrf_order_name: handleMrfOrderName(row.mrf_order_name),
        mrf_order_stage: handleStatusMessage(row.mrf_order_stage),
        mrf_order_status: handleStatusMessage(row.mrf_order_status),
        project_name: currentProjectMetadata?.project_name ? (
          currentProjectMetadata.project_name
        ) : (
          <Skeleton width="7rem" height="1.2rem" />
        ),
        updated: DateTime.fromJSDate(modDate).toFormat("F"),
        actions: handleActions(),
      }
    })
  }, [data, projectMetadata, deletingDataOrder])

  return (
    <div>
      <h1>Data Orders</h1>
      <Breadcrumb items={[{ label: "Data Orders" }]} />

      <div style={{ display: "flex", gap: 5, marginBottom: 15 }}>
        <Button
          onClick={() => navigate("/data-order/add")}
          className="btn-secondary"
        >
          Create New Data Order
        </Button>
        <Button onClick={() => navigate("/project/add")}>
          Create New Dataset
        </Button>
      </div>

      <DataTable
        value={handleData}
        responsiveLayout="scroll"
        loading={loading || fetching}
      >
        <Column field="project_name" header="Dataset" filter sortable />
        <Column
          field="mrf_order_name"
          header="Data Order Name"
          filter
          sortable
        />
        <Column field="mrf_order_stage" header="Stage" />
        <Column field="mrf_order_status" header="Status" />
        <Column field="updated" header="Last Order Date" sortable />
        <Column field="actions" header="Actions" style={{ width: 240 }} />
      </DataTable>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "1rem",
          marginTop: 16,
        }}
      >
        <Button
          label="Previous"
          icon="pi pi-angle-left"
          onClick={() => setCurrentPageIndex((i) => Math.max(i - 1, 0))}
          disabled={currentPageIndex === 0}
        />
        <Button
          label="Next"
          icon="pi pi-angle-right"
          iconPos="right"
          onClick={() => setCurrentPageIndex((i) => i + 1)}
          disabled={!lastVisible || !data || data.length < hookParams.limit}
        />
      </div>

      <Toast ref={toast} />
    </div>
  )
}
